import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Loader2,
  Key,
  Link2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// X (Twitter) authentic icon
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Instagram authentic icon
const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

// Facebook authentic icon
const FacebookIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// LinkedIn authentic icon
const LinkedInIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

// TikTok authentic icon
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

// YouTube authentic icon
const YouTubeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

// Pinterest authentic icon
const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
  </svg>
);

const platformConfig = {
  twitter: {
    name: "Twitter/X",
    icon: XIcon,
    color: "bg-black",
    textColor: "text-white",
    placeholder: "@usuario",
    description: "Conecta tu cuenta de Twitter para publicar tweets"
  },
  instagram: {
    name: "Instagram",
    icon: InstagramIcon,
    color: "bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400",
    textColor: "text-white",
    placeholder: "@usuario",
    description: "Conecta tu cuenta de Instagram Business"
  },
  facebook: {
    name: "Facebook",
    icon: FacebookIcon,
    color: "bg-blue-600",
    textColor: "text-white",
    placeholder: "Nombre de página",
    description: "Conecta tu página de Facebook"
  },
  linkedin: {
    name: "LinkedIn",
    icon: LinkedInIcon,
    color: "bg-sky-700",
    textColor: "text-white",
    placeholder: "Perfil o página",
    description: "Conecta tu perfil o página de LinkedIn"
  },
  tiktok: {
    name: "TikTok",
    icon: TikTokIcon,
    color: "bg-black",
    textColor: "text-white",
    placeholder: "@usuario",
    description: "Conecta tu cuenta de TikTok"
  },
  youtube: {
    name: "YouTube",
    icon: YouTubeIcon,
    color: "bg-red-600",
    textColor: "text-white",
    placeholder: "Nombre del canal",
    description: "Conecta tu canal de YouTube"
  },
  pinterest: {
    name: "Pinterest",
    icon: PinterestIcon,
    color: "bg-red-700",
    textColor: "text-white",
    placeholder: "@usuario",
    description: "Conecta tu cuenta de Pinterest para crear pins"
  }
};

export const SocialAccountsManager = ({ token, onAccountsChange }) => {
  const [accounts, setAccounts] = useState([]);
  const [oauthStatus, setOauthStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [accountName, setAccountName] = useState("");
  const [profileUrl, setProfileUrl] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [connectMode, setConnectMode] = useState("manual"); // "oauth" or "manual"

  useEffect(() => {
    fetchData();
    
    // Listen for OAuth callback messages
    const handleMessage = (event) => {
      if (event.data.type === 'oauth_success') {
        toast.success(`Cuenta de ${platformConfig[event.data.platform]?.name} conectada via OAuth`);
        fetchData();
        setConnectDialogOpen(false);
      } else if (event.data.type === 'oauth_error') {
        toast.error(`Error al conectar: ${event.data.error}`);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [accountsRes, oauthRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/social`, { headers }),
        axios.get(`${BACKEND_URL}/api/oauth/status`, { headers })
      ]);
      
      setAccounts(accountsRes.data);
      setOauthStatus(oauthRes.data.platforms || {});
      
      if (onAccountsChange) {
        onAccountsChange(accountsRes.data);
      }
    } catch (error) {
      console.error("Error fetching social accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthConnect = async () => {
    if (!selectedPlatform) return;
    
    setConnecting(true);
    try {
      // Special handling for Twitter - use owner's pre-configured account
      if (selectedPlatform === "twitter") {
        const response = await axios.post(
          `${BACKEND_URL}/api/social/connect-twitter-owner`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success(`Cuenta ${response.data.account_name} conectada exitosamente`);
        fetchData();
        setConnectDialogOpen(false);
        return;
      }
      
      const response = await axios.get(
        `${BACKEND_URL}/api/oauth/authorize/${selectedPlatform}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { authorization_url } = response.data;
      
      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      window.open(
        authorization_url,
        'oauth_popup',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al conectar";
      toast.error(errorMsg);
    } finally {
      setConnecting(false);
    }
  };

  const handleManualConnect = async () => {
    if (!selectedPlatform || !accountName.trim()) {
      toast.error("Ingresa el nombre de la cuenta");
      return;
    }

    setConnecting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/social/connect/${selectedPlatform}`,
        {
          platform: selectedPlatform,
          account_name: accountName,
          profile_url: profileUrl || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Cuenta de ${platformConfig[selectedPlatform].name} conectada`);
      setConnectDialogOpen(false);
      setSelectedPlatform(null);
      setAccountName("");
      setProfileUrl("");
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al conectar la cuenta";
      toast.error(errorMsg);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (accountId) => {
    if (!window.confirm("¿Estás seguro de que deseas desconectar esta cuenta?")) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/social/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cuenta desconectada");
      fetchData();
    } catch (error) {
      toast.error("Error al desconectar la cuenta");
    }
  };

  const getConnectedPlatforms = () => {
    return accounts.map(a => a.platform);
  };

  const openConnectDialog = (platformId) => {
    setSelectedPlatform(platformId);
    setAccountName("");
    setProfileUrl("");
    // Check if OAuth is configured for this platform
    const isOAuthConfigured = oauthStatus[platformId]?.oauth_configured;
    setConnectMode(isOAuthConfigured ? "oauth" : "manual");
    setConnectDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const connectedPlatforms = getConnectedPlatforms();

  return (
    <div className="space-y-6" data-testid="social-accounts-manager">
      {/* Connected Accounts */}
      {accounts.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Cuentas Conectadas</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {accounts.map((account) => {
              const config = platformConfig[account.platform];
              const Icon = config?.icon || Twitter;
              
              return (
                <Card key={account.account_id} className="relative overflow-hidden">
                  <div className={`absolute top-0 left-0 w-1 h-full ${config?.color || 'bg-gray-500'}`} />
                  <CardContent className="p-4 pl-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${config?.color || 'bg-gray-500'} ${config?.textColor || 'text-white'}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-semibold">{account.account_name}</h4>
                          <p className="text-sm text-muted-foreground capitalize">{config?.name || account.platform}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDisconnect(account.account_id)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`disconnect-${account.account_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant={account.connected ? "default" : "secondary"} className="gap-1">
                          {account.connected ? (
                            <>
                              <CheckCircle className="w-3 h-3" />
                              Conectada
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-3 h-3" />
                              Pendiente
                            </>
                          )}
                        </Badge>
                        {account.oauth_connected && (
                          <Badge variant="outline" className="gap-1 text-xs">
                            <Key className="w-3 h-3" />
                            OAuth
                          </Badge>
                        )}
                      </div>
                      {account.profile_url && (
                        <a 
                          href={account.profile_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline flex items-center gap-1"
                        >
                          Ver perfil
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Platforms to Connect */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">
          {accounts.length > 0 ? "Conectar más cuentas" : "Conectar Redes Sociales"}
        </h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Object.entries(platformConfig).map(([platformId, config]) => {
            const isConnected = connectedPlatforms.includes(platformId);
            const isOAuthConfigured = oauthStatus[platformId]?.oauth_configured;
            const Icon = config.icon;
            
            return (
              <Card 
                key={platformId}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  isConnected ? 'opacity-60' : ''
                }`}
                onClick={() => !isConnected && openConnectDialog(platformId)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl ${config.color} ${config.textColor}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{config.name}</h4>
                        {isOAuthConfigured && (
                          <Badge variant="outline" className="text-[10px] gap-1">
                            <Key className="w-2.5 h-2.5" />
                            OAuth
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {isConnected ? "Ya conectada" : "Clic para conectar"}
                      </p>
                    </div>
                    {isConnected ? (
                      <CheckCircle className="w-5 h-5 text-primary" />
                    ) : (
                      <Plus className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Connect Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedPlatform && (
                <>
                  <div className={`p-2 rounded-lg ${platformConfig[selectedPlatform]?.color} ${platformConfig[selectedPlatform]?.textColor}`}>
                    {(() => {
                      const Icon = platformConfig[selectedPlatform]?.icon;
                      return Icon ? <Icon className="w-5 h-5" /> : null;
                    })()}
                  </div>
                  Conectar {platformConfig[selectedPlatform]?.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {platformConfig[selectedPlatform]?.description}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPlatform && (
            <div className="space-y-4">
              {/* OAuth vs Manual toggle */}
              {oauthStatus[selectedPlatform]?.oauth_configured ? (
                <div className="flex gap-2 p-1 bg-secondary rounded-lg">
                  <Button
                    variant={connectMode === "oauth" ? "default" : "ghost"}
                    size="sm"
                    className="flex-1"
                    onClick={() => setConnectMode("oauth")}
                  >
                    <Key className="w-4 h-4 mr-2" />
                    OAuth (Recomendado)
                  </Button>
                  <Button
                    variant={connectMode === "manual" ? "default" : "ghost"}
                    size="sm"
                    className="flex-1"
                    onClick={() => setConnectMode("manual")}
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Manual
                  </Button>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    <strong>Nota:</strong> OAuth no está configurado para {platformConfig[selectedPlatform]?.name}. 
                    Puedes conectar manualmente ingresando el nombre de tu cuenta.
                  </p>
                </div>
              )}
              
              {connectMode === "oauth" && oauthStatus[selectedPlatform]?.oauth_configured ? (
                <div className="space-y-4">
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-center">
                    <Key className="w-8 h-8 mx-auto mb-2 text-primary" />
                    <p className="text-sm text-muted-foreground mb-4">
                      Serás redirigido a {platformConfig[selectedPlatform]?.name} para autorizar la conexión de forma segura.
                    </p>
                    <Button 
                      onClick={handleOAuthConnect}
                      disabled={connecting}
                      className="w-full"
                      data-testid="oauth-connect-btn"
                    >
                      {connecting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Conectando...
                        </>
                      ) : (
                        <>
                          <Key className="w-4 h-4 mr-2" />
                          Conectar con OAuth
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label>Nombre de la cuenta *</Label>
                    <Input
                      placeholder={platformConfig[selectedPlatform]?.placeholder}
                      value={accountName}
                      onChange={(e) => setAccountName(e.target.value)}
                      data-testid="account-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label>URL del perfil (opcional)</Label>
                    <Input
                      placeholder={`https://${selectedPlatform}.com/usuario`}
                      value={profileUrl}
                      onChange={(e) => setProfileUrl(e.target.value)}
                      data-testid="profile-url-input"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setConnectDialogOpen(false)}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      className="flex-1"
                      onClick={handleManualConnect}
                      disabled={connecting || !accountName.trim()}
                      data-testid="confirm-connect-btn"
                    >
                      {connecting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Conectando...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Conectar Cuenta
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
